/**
 * The hand-written fetch wrapper (deliberately no request-function codegen):
 * `openapi-typescript` provides the `paths` type, and the four verbs below are
 * typed against it, so a call site names a literal spec path and gets its
 * parameter, body and response types checked — while the runtime stays a
 * single small `request()`.
 */

import type { paths } from '@/api/schema';
import { errorForStatus, NetworkError } from '@/api/errors';
import { config } from '@/config';
import { getAccessToken } from '@/features/auth/token';

type Method = 'get' | 'post' | 'put' | 'delete';

/**
 * The `paths`-derived types below key off a concrete per-method operation
 * map (`GetPaths`, `PostPaths`, ...) instead of a single type generic over
 * both `P extends keyof paths` and `M`. With ~50 paths across four methods,
 * `openapi-typescript`'s output makes that combined conditional too complex
 * for `tsc` to represent (TS2590) once `/statistics/*`, `/dashboards` and
 * `/subscriptions/*` were added — and a generic indexed access built from
 * two type parameters (`MethodPaths<M>[P]`) doesn't reliably narrow either
 * (TS2536). Resolving each method's map once, as its own named type, keeps
 * every lookup below a plain index into a concrete object type.
 */
type MethodPaths<M extends Method> = {
  [P in keyof paths as undefined extends paths[P][M] ? never : P]: Exclude<paths[P][M], undefined>;
};

type GetPaths = MethodPaths<'get'>;
type PostPaths = MethodPaths<'post'>;
type PutPaths = MethodPaths<'put'>;
type DeletePaths = MethodPaths<'delete'>;

type PathParamsOf<Ops, P extends keyof Ops> = Ops[P] extends { parameters: { path: infer PP } }
  ? PP
  : undefined;

type QueryOf<Ops, P extends keyof Ops> = Ops[P] extends { parameters: { query?: infer Q } }
  ? Q
  : undefined;

type BodyOf<Ops, P extends keyof Ops> = Ops[P] extends {
  requestBody: { content: { 'application/json': infer B } };
}
  ? B
  : Ops[P] extends { requestBody?: { content: { 'application/json': infer B } } }
    ? B | undefined
    : undefined;

/** The JSON of the operation's 200/201 response; `undefined` for bodyless (204 or no schema). */
type SuccessJson<Ops, P extends keyof Ops> = Ops[P] extends { responses: infer R }
  ? R extends { 200: { content: { 'application/json': infer J200 } } }
    ? J200
    : R extends { 201: { content: { 'application/json': infer J201 } } }
      ? J201
      : undefined
  : undefined;

interface RequestOptions<Ops, P extends keyof Ops> {
  /** Values for the `{placeholders}` in the path. */
  params?: PathParamsOf<Ops, P>;
  query?: QueryOf<Ops, P>;
  body?: BodyOf<Ops, P>;
  signal?: AbortSignal;
}

/**
 * Substitutes `{param}` placeholders and appends the query string. Throws on a
 * placeholder without a value — that is a programming error at the call site,
 * not a server condition.
 */
export function buildUrl(
  path: string,
  params?: Record<string, unknown>,
  query?: Record<string, unknown>,
): string {
  const substituted = path.replace(/\{([^}]+)\}/g, (_, name: string) => {
    const value = params?.[name];
    if (value === undefined || value === null) {
      throw new Error(`Missing path parameter "${name}" for ${path}`);
    }
    return encodeURIComponent(String(value));
  });

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null) search.append(key, String(value));
  }
  const qs = search.toString();
  return `${config.apiUrl}${substituted}${qs ? `?${qs}` : ''}`;
}

async function request<Ops, P extends keyof Ops & string>(
  method: Method,
  path: P,
  options: RequestOptions<Ops, P> = {},
): Promise<SuccessJson<Ops, P>> {
  const url = buildUrl(
    path,
    options.params as Record<string, unknown> | undefined,
    options.query as Record<string, unknown> | undefined,
  );

  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(url, {
      method: method.toUpperCase(),
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : null,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new NetworkError(`Request to ${path} failed: ${String(cause)}`);
  }

  if (!response.ok) {
    throw errorForStatus(
      response.status,
      `${method.toUpperCase()} ${path} failed`,
      await readReason(response),
    );
  }

  if (response.status === 204) return undefined as SuccessJson<Ops, P>;
  const text = await response.text();
  if (text === '') return undefined as SuccessJson<Ops, P>;
  return JSON.parse(text) as SuccessJson<Ops, P>;
}

/** The API reports failures as `{"error": ..., "reason": ...}`. */
export async function readReason(response: Response): Promise<string | undefined> {
  try {
    const body: unknown = await response.json();
    if (typeof body === 'object' && body !== null) {
      const record = body as Record<string, unknown>;
      const reason = record.reason ?? record.error ?? record.message;
      if (typeof reason === 'string') return reason;
    }
  } catch {
    // Non-JSON error body; the status code alone will have to do.
  }
  return undefined;
}

export function apiGet<P extends keyof GetPaths & string>(
  path: P,
  options?: RequestOptions<GetPaths, P>,
): Promise<SuccessJson<GetPaths, P>> {
  return request<GetPaths, P>('get', path, options);
}

export function apiPost<P extends keyof PostPaths & string>(
  path: P,
  options?: RequestOptions<PostPaths, P>,
): Promise<SuccessJson<PostPaths, P>> {
  return request<PostPaths, P>('post', path, options);
}

export function apiPut<P extends keyof PutPaths & string>(
  path: P,
  options?: RequestOptions<PutPaths, P>,
): Promise<SuccessJson<PutPaths, P>> {
  return request<PutPaths, P>('put', path, options);
}

export function apiDelete<P extends keyof DeletePaths & string>(
  path: P,
  options?: RequestOptions<DeletePaths, P>,
): Promise<SuccessJson<DeletePaths, P>> {
  return request<DeletePaths, P>('delete', path, options);
}
